import { useEffect, type JSX, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AdminSidebar } from "./admin/components/AdminSidebar.js";
import { AdminPage } from "./admin/pages/AdminPage.js";
import type { AppDescriptorDTO, MeDTO } from "./common/api/types.js";
import { AppMissingPage } from "./common/apps/AppMissingPage.js";
import { unimplementedApps } from "./common/apps/appRoutes.js";
import { useAuth } from "./common/auth/AuthProvider.js";
import { WelcomePage } from "./common/auth/WelcomePage.js";
import { AppShell } from "./common/components/AppShell.js";
import type { MobileStart } from "./common/components/panelChoice.js";
import { PrimaryButton } from "./common/components/PrimaryButton.js";
import { StatusScreen } from "./common/components/StatusScreen.js";
import { HomePage } from "./home/pages/HomePage.js";
import { MODULES } from "./modules/index.js";
import { PublicPasswordPage } from "./modules/passwords/pages/PublicPasswordPage.js";
import { OnboardingPage } from "./onboarding/pages/OnboardingPage.js";
import { useApps } from "./tenant/query/appsQueries.js";
import { useMe, useSelectTenant } from "./tenant/query/meQueries.js";

export function App(): JSX.Element {
  const publicPasswordToken = publicPasswordTokenFromPath(window.location.pathname);
  const auth = useAuth();
  const authenticated = auth.status === "authenticated";
  const me = useMe(authenticated);
  const selectTenant = useSelectTenant();
  const apps = useApps(authenticated && me.data?.activeTenant != null);
  const theme = me.data?.settings.theme ?? "system";

  useEffect(() => {
    document.documentElement.classList.toggle("theme-light", theme === "light");
    document.documentElement.classList.toggle("theme-dark", theme === "dark");
  }, [theme]);

  if (publicPasswordToken) return <PublicPasswordPage token={publicPasswordToken} />;

  if (auth.status === "loading" || selectTenant.isPending || (authenticated && me.isPending)) {
    return <StatusScreen title="Einen Moment …" loading />;
  }

  if (!authenticated) {
    return <WelcomePage />;
  }

  if (me.isError || !me.data) {
    return (
      <StatusScreen
        title="Anmeldung unvollständig"
        description={me.error?.message ?? "Die Benutzerdaten konnten nicht geladen werden."}
        action={<PrimaryButton onClick={() => void auth.signOut()}>Abmelden</PrimaryButton>}
      />
    );
  }

  // Wer noch zu keinem Unternehmen gehört, legt zuerst eines an (Issue #30).
  if (me.data.memberships.length === 0) {
    return <OnboardingPage />;
  }

  // Mehrere Mitgliedschaften ohne Auswahl: der Server entscheidet bewusst nicht für uns.
  if (!me.data.activeTenant) {
    return (
      <StatusScreen
        title="Mandant wählen"
        description="Dieses Konto gehört zu mehreren Unternehmen."
        action={
          <div className="space-y-2">
            {me.data.memberships.map((membership) => (
              <PrimaryButton
                key={membership.id}
                className="w-full"
                onClick={() => selectTenant.mutate(membership.tenant.slug)}
              >
                {membership.tenant.name}
              </PrimaryButton>
            ))}
          </div>
        }
      />
    );
  }

  if (apps.isPending) return <StatusScreen title="Apps werden geladen …" loading />;
  if (apps.isError) {
    return <StatusScreen title="Apps konnten nicht geladen werden" description={apps.error.message}
      action={<PrimaryButton onClick={() => void apps.refetch()}>Erneut versuchen</PrimaryButton>} />;
  }

  const appList = apps.data;
  const openable = new Set(appList.map((app) => app.route));
  const shell = {
    me: me.data,
    apps: appList,
    onSelect: selectTenant.mutate,
    busy: selectTenant.isPending,
    signOut: () => void auth.signOut(),
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          // Die Startseite hat keine eigene Sidebar; der Inhalt nimmt die ganze Breite.
          <Shell title="Start" baseRoute="/" {...shell}>
            <HomePage me={me.data} apps={appList} />
          </Shell>
        }
      />
      <Route
        path="/admin"
        element={
          openable.has("/admin") ? <Shell title="Administration" baseRoute="/admin" {...shell} sidebar={<AdminSidebar />}>
            <AdminPage me={me.data} />
          </Shell> : <Navigate to="/" replace />
        }
      />

      {/* Fach-Module. Eine Route entsteht nur, wenn die Registry sie für diesen
          Benutzer freigibt — sonst landet der Aufruf auf der Startseite. Module mit
          einem `detailRoute` bekommen zusätzlich die Route für den einzelnen Datensatz. */}
      {MODULES.filter((module) => openable.has(module.route)).flatMap((module) => {
        const Page = module.page;
        const Sidebar = module.sidebar;
        const element = (
          <Shell
            title={module.title}
            baseRoute={module.route}
            {...shell}
            mobileStart={module.mobileStart}
            sidebar={Sidebar ? <Sidebar me={me.data} /> : undefined}
          >
            <Page me={me.data} />
          </Shell>
        );
        const routes = [<Route key={module.id} path={module.route} element={element} />];
        if (module.detailRoute) {
          routes.push(
            <Route
              key={`${module.id}-detail`}
              path={`${module.route}/${module.detailRoute}`}
              element={element}
            />,
          );
        }
        return routes;
      })}

      {/* Apps, die der Server anbietet und die Oberfläche noch nicht kann: lieber eine
          Seite, die das sagt, als eine wortlose Umleitung auf die Startseite. */}
      {unimplementedApps(appList, MODULES.map((module) => module.route)).map((app) => (
        <Route
          key={app.id}
          path={app.route}
          element={
            <Shell title={app.name} baseRoute={app.route} {...shell}>
              <AppMissingPage name={app.name} />
            </Shell>
          }
        />
      ))}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function publicPasswordTokenFromPath(pathname: string): string | null {
  const match = /^\/passwords\/public\/([A-Za-z0-9_-]+)\/?$/.exec(pathname);
  return match?.[1] ?? null;
}

function Shell({
  title,
  baseRoute,
  me,
  apps,
  onSelect,
  busy,
  signOut,
  sidebar,
  mobileStart,
  children,
}: {
  title: string;
  baseRoute: string;
  me: MeDTO;
  apps: AppDescriptorDTO[];
  onSelect: (slug: string) => void;
  busy: boolean;
  signOut: () => void;
  /** Sidebar des Moduls. Ohne Angabe nimmt der Inhalt die ganze Breite. */
  sidebar?: ReactNode;
  /** Womit das Telefon aufmacht; kommt aus der Modul-Definition. */
  mobileStart?: MobileStart;
  children: ReactNode;
}): JSX.Element {
  return (
    <AppShell
      me={me}
      apps={apps}
      title={title}
      baseRoute={baseRoute}
      moduleSidebar={sidebar ?? null}
      onSelectTenant={onSelect}
      switchingTenant={busy}
      onSignOut={signOut}
      mobileStart={mobileStart}
    >
      {children}
    </AppShell>
  );
}
